import uuid from "uuid";

import * as types from '../actions/actionTypes';
import featureIsMatchingDisplayLogic from "../misc/displayLogic";
import {COUNTERTOP_GROUP} from "../bvcomponents/kvComponent/core/constants";
import {getMinimumChargeCartItem} from "../actions/shoppingCartFunctions";
import {FEATURE_STONE, getFeatureType} from "../misc/helpers";
import {IS_LIXIL_UI} from "../config";
import {getFeaturePriceProperty} from "../misc/priceFunctions";
import {SQFT_FEATURES} from "../bvcomponents/sqft/constants";
import {hardCodeIsAmericanStandard} from "../misc/fabricatorHardcode";

const initialState = {
  cartItems: [],
  displayLogicCartItems: [], // read only (is needed to check them every time cart is changed)
  applyPopupOpened: false,
  activeApplyPopupItems: [],
  questionNoClicked: false,
  cameFromPage: '',
  recentlyViewedItems: [],
  checkoutPopupVisible: false,
  orderCompletePopupVisible: false,
  depositVisible: false,
  depositCallback: null,
  state: 'design',
  showMobileQuotes: false,
  showAllQuotesCartItemsList: true,
  cartFooterActiveButtonIndex: 0,
  cartItemEditFeature: null,
  lookRemovedCartItems: [],
  cItemsAddedToParentCart: [], // ArrayOf {id: "string", quantity: number}
  sureTax: 0, // comes in response of sureTax API
  sureTaxLoading: false, // shows loader in columns section
  manualTaxEntered: false, // if it is true - show always the same manual tax
  deliveryList: [], // calculates depending on features shipping cost // {cost, title: "", delivery: "", code: ""}
  markUpLogic: "", // {retail: {value: 10, type: "mark_up"}, cost: {value: 10, type: "mark_down"}, discount: {}}
  hideTotalPricesBeforeWSOpened: hardCodeIsAmericanStandard(), // for AS we hide until someone click Price Worksheet
};

export const supportedCountries = [{
  name: 'USA',
  zipCodeRg: /^\d{5}([ \-]\d{4})?$/
}, {
  name: 'Canada',
  zipCodeRg: /^[ABCEGHJKLMNPRSTVXY]\\d[ABCEGHJ-NPRSTV-Z][ ]?\\d[ABCEGHJ-NPRSTV-Z]\\d$/
}, {
  name: 'UK',
  zipCodeRg: /^GIR[ ]?0AA|((AB|AL|B|BA|BB|BD|BH|BL|BN|BR|BS|BT|CA|CB|CF|CH|CM|CO|CR|CT|CV|CW|DA|DD|DE|DG|DH|DL|DN|DT|DY|E|EC|EH|EN|EX|FK|FY|G|GL|GY|GU|HA|HD|HG|HP|HR|HS|HU|HX|IG|IM|IP|IV|JE|KA|KT|KW|KY|L|LA|LD|LE|LL|LN|LS|LU|M|ME|MK|ML|N|NE|NG|NN|NP|NR|NW|OL|OX|PA|PE|PH|PL|PO|PR|RG|RH|RM|S|SA|SE|SG|SK|SL|SM|SN|SO|SP|SR|SS|ST|SW|SY|TA|TD|TF|TN|TQ|TR|TS|TW|UB|W|WA|WC|WD|WF|WN|WR|WS|WV|YO|ZE)(\d[\dA-Z]?[ ]?\d[ABD-HJLN-UW-Z]{2}))|BFPO[ ]?\d{1,4}$/
}];
export const supportedCountriesList = ["USA", "Canada", "UK"];

function checkForAddToCartFeaturesWithDisplayLogic(cartItems,
                                                   displayLogicCartItems,
                                                   currentQuote = 0,
                                                   allWizardsValues,
                                                   fabricatorState) {
  cartItems = cartItems.filter(cartItem => {
    return !displayLogicCartItems.some(displayLogicCartItem => {
      return (cartItem.cartFeature.dbFeature.id === displayLogicCartItem.cartFeature.dbFeature.id)
        && (cartItem.currentQuote === currentQuote)
    })
  }); // clear existing display logic items for this quote, we will add them below
  displayLogicCartItems.forEach((displayLogicCartItem) => {
    const {quantity, isMatching} = featureIsMatchingDisplayLogic(
      displayLogicCartItem.cartFeature,
      cartItems,
      currentQuote,
      allWizardsValues,
      displayLogicCartItems,
      fabricatorState
    );
    if (isMatching) {
      cartItems = [...cartItems, Object.assign({}, displayLogicCartItem, {
        quantity,
        currentQuote,
        id: uuid.v4(), // to make sure we have different cart items in different quotes
      })]
    }
  });
  return cartItems
}

function removeFakePriceFromCartItems(cartItems) {
  cartItems = cartItems.map((cartItem) => {
    if (cartItem.displayFakePrice === 0) {
      cartItem.displayFakePrice = null
    }
    return cartItem
  });
  return cartItems
}

const checkLowesHardcodedRulesItems = (cartItems, fabricatorSettings) => {
  cartItems = cartItems.filter(cItem => !cItem.isMinimumCharge); // 1) remove already added, and calculate again
  const countertopItems = cartItems.filter(cItem => COUNTERTOP_GROUP.includes(cItem.partId)); // 2) countertop group only

  const MINIMUM_SQFT = 23;
  const MINIMUM_SQFT_2 = 27;

  if (countertopItems.length) {
    const totalQuantity = countertopItems.reduce(((accumulator, cItem) => accumulator + cItem.quantity), 0); // 3) get all quantities
    if (totalQuantity <= MINIMUM_SQFT) {
      cartItems.push(getMinimumChargeCartItem(countertopItems[0], true))  // 4) add hardcoded 300$
    } else if ((totalQuantity >= (MINIMUM_SQFT + 1)) && (totalQuantity <= (MINIMUM_SQFT_2 -1))) { // 5) between 23 and 27 - add additional countertops
      let theMostValuableCartItem = countertopItems[0];
      countertopItems.forEach(cItem => {
        let currentMostFeatureCost = getFeaturePriceProperty(theMostValuableCartItem.cartFeature, fabricatorSettings) * theMostValuableCartItem.quantity;
        let cartItemCost = getFeaturePriceProperty(cItem.cartFeature, fabricatorSettings) * cItem.quantity;
        if (cartItemCost > currentMostFeatureCost) {  // 6) find with the biggest price added
          theMostValuableCartItem = cItem
        }
      });
      cartItems.push(getMinimumChargeCartItem(theMostValuableCartItem, false, MINIMUM_SQFT_2 - totalQuantity)) // 7) add hardcoded items
    }
  }
  // console.log("cartItems2", cartItems);
  return cartItems
}

const checkCaesHardcodedRulesItems = (cartItems) => {
  // console.log("cartItems before", cartItems);
  cartItems = cartItems.filter(cItem => !cItem.isMinimumCharge); // 1) remove already added, and calculate again
  const countertopItems = cartItems.filter(cItem => COUNTERTOP_GROUP.includes(cItem.partId)); // 2) countertop group only
  let uniqueSharedNames = [];
  countertopItems.forEach(cItem => {
    if (!uniqueSharedNames.includes(cItem.cartFeature.dbFeature.sharedFeatureName)) {
      uniqueSharedNames.push(cItem.cartFeature.dbFeature.sharedFeatureName)
    }
  });
  const MINIMUM_CAES_SQFT = 25;

  if (uniqueSharedNames.length) {
    uniqueSharedNames.forEach(uniqueSharedName => {
      const matchingItems = countertopItems.filter(cItem => cItem.cartFeature.dbFeature.sharedFeatureName === uniqueSharedName);
      const totalQuantity = matchingItems.reduce(((accumulator, cItem) => accumulator + cItem.quantity), 0); // 3) get all quantities
      if (totalQuantity < MINIMUM_CAES_SQFT) {
        cartItems.push(getMinimumChargeCartItem(matchingItems[0], false, MINIMUM_CAES_SQFT - totalQuantity))
      }
    })
  }
  // console.log("cartItems2", cartItems);
  return cartItems
}

const allowMinimumChargeConditions = (fabricatorState) => {
  return fabricatorState && fabricatorState.settings.showPricing
    && !fabricatorState.hideCheckoutBreakdownCost && !fabricatorState.hidePricesFromUser
    && document.kvConfig.visualizerType === "Kitchen" || document.kvConfig.visualizerType === "kvScene"
}

const checkHardcodedRulesItems = (fabricatorState, cartItems) => {
  if (allowMinimumChargeConditions(fabricatorState)) {
    // console.log("cartItems", cartItems);
    // console.log("fabricatorState", fabricatorState);
    // console.log("fabricatorState.settings.language", fabricatorState.settings.language);
    if (fabricatorState.isLowes) {
      cartItems = checkLowesHardcodedRulesItems(cartItems, fabricatorState.settings);
    } else if (fabricatorState.isCaesarStone && (fabricatorState.settings.language !== "en-AU")) {
      cartItems = checkCaesHardcodedRulesItems(cartItems)
    }
  }
  cartItems = cartItems.map(cartItem => {
    if (cartItem.bundleParentId) {
      const parentItem = cartItems.find(cItem => cItem.id === cartItem.bundleParentId);
      const quantity = parentItem?.cartFeature.dbFeature.bundledFeatureQuantities
        ? parentItem.cartFeature.dbFeature.bundledFeatureQuantities[cartItem.cartFeature.dbFeature.id] || 1
        : 1;
      cartItem.quantity = parentItem ? quantity * parentItem.quantity : cartItem.quantity;
    }
    return cartItem
  })
  return cartItems
}

/**
 * Recalculate slab in cart
 * @param {{}} cartItems
 * return bool
 */
export const isRecalculateSlabsInCartNeeded = (cartItems) => {
  return cartItems.some(cartItem => {
    return cartItem.cartFeature && FEATURE_STONE === getFeatureType(cartItem.cartFeature.dbFeature)
  });
}

/**
 * Recalculate slab in cart
 * @param cartItems {object}
 * @returns cartItems {object}
 */
const recalculateSlabsInCart = (cartItems) => {
  for (const cartItem of cartItems) {
    if (FEATURE_STONE !== getFeatureType(cartItem.cartFeature.dbFeature)) {
      continue;
    }
    const similarFeatures = cartItems.filter((cItem) => {
      const isSameNameFeature = cItem.cartFeature.getFeatureName() === cartItem.cartFeature.getFeatureName();
      const isBiggestFeature = cItem.isBiggestFeature;
      const isQuantityAddedToBiggestFeature = cItem.isQuantityAddedToBiggestFeature;
      return isSameNameFeature && (isBiggestFeature || isQuantityAddedToBiggestFeature) // select only features from sqft
    });
    let biggestFeature = similarFeatures.find(cartItem => cartItem.isBiggestFeature);
    if (!biggestFeature && cartItem.isQuantityAddedToBiggestFeature) {
      cartItem.isBiggestFeature = true;
    }
    if (cartItem.isBiggestFeature) {
      cartItem.quantity = similarFeatures.reduce((sum, cItem) => {
        return cItem.displayFakeQuantity !== undefined
          ? cItem.displayFakeQuantity + sum
          : cItem.quantity + sum
      }, 0);
    }
  }
  return cartItems
}

const applyCartRulesAfterChange = (state, items, action) => {
  // 1) add/remove display-logic items for the current quote
  if (state.displayLogicCartItems.length) {
    const currentQuote =
      items.length
        ? (items[0].currentQuote ?? 0)
        : 0;

    items = checkForAddToCartFeaturesWithDisplayLogic(
      items,
      state.displayLogicCartItems,
      currentQuote,
      action.payload.allWizardsValues,
      action.payload.fabricatorState
    );
  }

  // 2) hardcoded fabricator rules
  items = checkHardcodedRulesItems(action.payload.fabricatorState, items);

  // 3) slabs recalculation (and related fake prices cleanup)
  if (isRecalculateSlabsInCartNeeded(items)) {
    items = recalculateSlabsInCart(items);
  }
  const slabsStillExist = items.some(ci => ci.partId === SQFT_FEATURES.slabs);
  if (!slabsStillExist) {
    items = removeFakePriceFromCartItems(items);
  }

  // 4) drop orphan bundle children if parent removed
  items = items.filter(ci => !ci.bundleParentId || items.some(p => p.id === ci.bundleParentId));

  return items;
}
// const checkStonePricingLogic = (cartItems) => { //TODO[Artem] edit features not working :(
//   if (cartItems.some(cartItem => {
//     return cartItem.cartFeature && Array.isArray(cartItem.cartFeature.dbFeature.stonePricingId) && cartItem.cartFeature.dbFeature.stonePricingId.length
//   })) {
//     let commonSharedFeatureNames = []; // {sharedName: "", totalQuantity: 0, biggestFeature, sqftFeatures: []}
//     cartItems.forEach(cartItem => {
//       if (!cartItem.initialQuantity) {
//         cartItem.initialQuantity = cartItem.quantity;
//       }
//       if (cartItem.cartFeature && Array.isArray(cartItem.cartFeature.dbFeature.stonePricingId)
//         && cartItem.cartFeature.dbFeature.stonePricingId.length) {
//         const alreadyAdded = commonSharedFeatureNames.find(sharedObject => {
//           return sharedObject.sharedName === cartItem.cartFeature.dbFeature.sharedFeatureName
//         });
//         if (!alreadyAdded) {
//           commonSharedFeatureNames.push({
//             sharedName: cartItem.cartFeature.dbFeature.sharedFeatureName,
//             totalQuantity: cartItem.initialQuantity,
//             sqftFeatures: [cartItem],
//             biggestFeature: cartItem,
//           })
//         } else {
//           alreadyAdded.sqftFeatures.push(cartItem);
//           alreadyAdded.totalQuantity += cartItem.initialQuantity;
//           if (!alreadyAdded.biggestFeature) {
//             alreadyAdded.biggestFeature = cartItem;
//           } else if (cartItem.cartFeature.partId === "countertop") {
//             if (alreadyAdded.biggestFeature.partId !== "countertop" || (alreadyAdded.biggestFeature.initialQuantity < cartItem.initialQuantity)) {
//               alreadyAdded.biggestFeature = cartItem;
//             }
//           } else if (cartItem.cartFeature.partId === "center_island_countertop") {
//             if ((alreadyAdded.biggestFeature.partId !== "countertop" && alreadyAdded.biggestFeature.partId !== "center_island_countertop")
//               || (alreadyAdded.biggestFeature.partId === "center_island_countertop" && alreadyAdded.biggestFeature.initialQuantity < cartItem.initialQuantity)) {
//               alreadyAdded.biggestFeature = cartItem;
//             }
//           } else if ((alreadyAdded.biggestFeature.partId !== "countertop") && (alreadyAdded.biggestFeature.partId !== "center_island_countertop")
//             && alreadyAdded.biggestFeature.initialQuantity < cartItem.initialQuantity) { // backsplash
//             alreadyAdded.biggestFeature = cartItem;
//           }
//         }
//       }
//     });
//     if (commonSharedFeatureNames.length) {  // 2) set each feature new fake quantity
//       commonSharedFeatureNames.forEach(sharedObject => {
//         sharedObject.sqftFeatures.forEach(cartItem => {
//           cartItem.displayFakeQuantity = cartItem.initialQuantity;
//           if (sharedObject.biggestFeature && (cartItem.id === sharedObject.biggestFeature.id)) {
//             cartItem.quantity = sharedObject.totalQuantity;
//           } else {
//             cartItem.quantity = 0;
//           }
//         });
//       });
//     }
//   }
//   return cartItems
// }

export default function(state = initialState, action) {

  switch(action.type) {

    case types.ADD_TO_CART: {
      const actionCartItems = action.payload.cartItems;
      let stateCartItems = state.cartItems;
      if (IS_LIXIL_UI) {
        stateCartItems = stateCartItems.map(stateCartItem => {
          const sameCartItemIndex = actionCartItems.findIndex(newCartItem => {
            return newCartItem.cartFeature && stateCartItem.cartFeature &&
              (newCartItem.cartFeature.dbFeature.sku && stateCartItem.cartFeature.dbFeature.sku
                ? (newCartItem.cartFeature.dbFeature.sku === stateCartItem.cartFeature.dbFeature.sku)
                : (newCartItem.cartFeature.dbFeature.sharedFeatureName
                  && stateCartItem.cartFeature.dbFeature.sharedFeatureName
                  && (newCartItem.cartFeature.dbFeature.sharedFeatureName === stateCartItem.cartFeature.dbFeature.sharedFeatureName)))
          })
          if (sameCartItemIndex > -1) {
            if (stateCartItem.addedBy !== actionCartItems[sameCartItemIndex].addedBy) {
              stateCartItem.quantity = actionCartItems[sameCartItemIndex].quantity || 1;
              stateCartItem.addedBy = actionCartItems[sameCartItemIndex].addedBy;
            } else {
              stateCartItem.quantity += actionCartItems[sameCartItemIndex].quantity || 1;
              if (actionCartItems[sameCartItemIndex].hasOwnProperty("isRemoved")) {
                stateCartItem.isRemoved = actionCartItems[sameCartItemIndex].isRemoved;
              }
            }
            actionCartItems.splice(sameCartItemIndex, 1);
          }
          return stateCartItem
        })
      }
      let newCartItems = [...stateCartItems, ...actionCartItems];

      if (state.displayLogicCartItems.length) {
        const currentQuote = actionCartItems.length
          ? actionCartItems[0].currentQuote
          : newCartItems.length
            ? newCartItems[0].currentQuote
            : 0;
        newCartItems = checkForAddToCartFeaturesWithDisplayLogic(
          newCartItems,
          state.displayLogicCartItems,
          currentQuote,
          action.payload.allWizardsValues,
          action.payload.fabricatorState
        );
      }
      newCartItems = checkHardcodedRulesItems(action.payload.fabricatorState, newCartItems);
      // newCartItems = checkStonePricingLogic(newCartItems);
      return Object.assign({}, state, {
        cartItems: newCartItems
      });
    }
    case types.SET_STATE_TO_QUOTE:
      return Object.assign({}, state, {
        state: "quote"
      });

    case types.ADD_DISPLAY_LOGIC_CART_ITEM:
      return Object.assign({}, state, {
        displayLogicCartItems: [...state.displayLogicCartItems.filter((oldCartItem) => {
          return (oldCartItem.cartFeature.dbFeature.id !== action.payload.cartItem.cartFeature.dbFeature.id)
            // && (oldCartItem.currentQuote !== action.payload.cartItem.currentQuote)
          // removed, why it is added? we can have different add to cart features in the same quote
        }), action.payload.cartItem]
      });

    case types.ADD_TO_RECENTLY_VIEWED:
      // console.log('action.payload.cartItem', action.payload.cartItem);
      return Object.assign({}, state, {
        recentlyViewedItems: (state.recentlyViewedItems.every(oldItem => oldItem.dbFeature.id !== action.payload.feature.dbFeature.id))
          ? [...state.recentlyViewedItems, action.payload.feature]
          : [...state.recentlyViewedItems]
      });

    case types.CHANGE_CART_QUANTITY: {
      // console.log('action.payload.cartItem', action.payload.cartItem);
      let newCartItems = [...state.cartItems.map((cartItem) => {
        if (cartItem.id === action.payload.cartItem.id) {
          cartItem.quantity = action.payload.quantity;
          if (action.payload.removeFakeQuantity && Number.isInteger(cartItem.displayFakeQuantity)) {
            cartItem.displayFakeQuantity = null;
          }
        }
        return cartItem
      })];
      if (state.displayLogicCartItems.length) {
        newCartItems = checkForAddToCartFeaturesWithDisplayLogic(
          newCartItems,
          state.displayLogicCartItems,
          action.payload.cartItem.currentQuote,
          action.payload.allWizardsValues,
          action.payload.fabricatorState,
        )
      }
      newCartItems = checkHardcodedRulesItems(action.payload.fabricatorState, newCartItems);
      // newCartItems = checkStonePricingLogic(newCartItems);
      return Object.assign({}, state, {
        cartItems: newCartItems
      });
    }

    case types.CHANGE_CART_FEATURE: {
      // console.log('action.payload.cartItem', action.payload.cartItem);
      let newCartItems = [...state.cartItems.map((cartItem) => {
        return (cartItem.id === action.payload.cartItem.id) ? action.payload.cartItem : cartItem
      })];
      if (!action.payload.skipOtherRules) { // if we change note text for example, we don't need to recalculate other cart items
        newCartItems = checkHardcodedRulesItems(action.payload.fabricatorState, newCartItems);
      }
      if (state.displayLogicCartItems.length) {
        newCartItems = checkForAddToCartFeaturesWithDisplayLogic(
          newCartItems,
          state.displayLogicCartItems,
          action.payload.cartItem.currentQuote,
          action.payload.allWizardsValues,
          action.payload.fabricatorState,
        )
      }
      return Object.assign({}, state, {
        cartItems: newCartItems
      });
    }

    case types.REMOVE_FROM_CART: {
      const id = action.payload.cartItem.id;
      let newCartItems = state.cartItems.filter(ci => ci.id !== id);
      newCartItems = applyCartRulesAfterChange(state, newCartItems, action);
      return { ...state, cartItems: newCartItems };
    }

    case types.REMOVE_MANY_FROM_CART: {
      const toRemove = new Set(action.payload.ids);
      let newCartItems = state.cartItems.filter(ci => !toRemove.has(ci.id));

      newCartItems = applyCartRulesAfterChange(state, newCartItems, action);

      return { ...state, cartItems: newCartItems };
    }

    case types.TOGGLE_APPLY_POPUP:
      return Object.assign({}, state, {
        applyPopupOpened: action.payload.applyPopupOpened,
        activeApplyPopupItems: action.payload.activeApplyPopupItems.map((activeApplyPopupItem) => {
          const existingCartItemIndex = state.cartItems.findIndex(cartItem => {
            return cartItem.cartFeature.dbFeature.id === activeApplyPopupItem.cartFeature.dbFeature.id
          });
          if (existingCartItemIndex > -1) {
            activeApplyPopupItem.quantity = state.cartItems[existingCartItemIndex].quantity; // if cart already have item, return it's quantity
          }
          return activeApplyPopupItem
        }).filter(item => {
          return !(item.partId === "top_cabinet"
              && action.payload.activeApplyPopupItems.some(item => item.partId === "top_cabinet_module_0"));

        })
      });

    case types.CLICK_QUESTION_NO:
    case types.CAME_FROM_PAGE:
    case types.SHOW_ORDER_COMPLETE_POPUP:
    case types.TOGGLE_SHOW_MOBILE_QUOTES:
    case types.TOGGLE_SHOW_ALL_QUOTES_CART_ITEMS_LIST:
    case types.SET_CART_FOOTER_ACTIVE_BUTTON_INDEX:
    case types.SET_CART_ITEM_EDIT_FEATURE:
    case types.SET_LOOK_REMOVED_CART_ITEMS:
    case types.CHANGE_ALL_CART_ITEMS:
    case types.SET_SURE_TAX:
    case types.SET_DELIVERY_LIST:
    case types.SET_SURE_TAX_LOADING:
    case types.SET_MARK_UP_LOGIC:
      return Object.assign({}, state, {
        ...action.payload
      });
    case types.RESTORE_CART: {
      if (action.payload.cartState && action.payload.cartState.cartItems) {
        action.payload.cartState.cartItems = checkHardcodedRulesItems(action.payload.fabricatorState, action.payload.cartState.cartItems);
        // action.payload.cartState.cartItems = checkStonePricingLogic(action.payload.cartState.cartItems);
      }
      let displayLogicCartItems = action.payload.cartState && action.payload.cartState.displayLogicCartItems
        ? action.payload.cartState.displayLogicCartItems
        : [];
      if ((displayLogicCartItems && displayLogicCartItems.length) || state.displayLogicCartItems.length) {
        action.payload.cartState.displayLogicCartItems = [...displayLogicCartItems, ...state.displayLogicCartItems.filter((oldCartItem) => {
          return !displayLogicCartItems.some(newCi => newCi.cartFeature.dbFeature.id === oldCartItem.cartFeature.dbFeature.id)
        })];
        // cases when someone loaded display logic in bathroom, then saved kitchen quote, and when the kitchen is restored they are loaded to kitchen
        action.payload.cartState.cartItems = checkForAddToCartFeaturesWithDisplayLogic(
          action.payload.cartState.cartItems,
          action.payload.cartState.displayLogicCartItems,
          0,
          action.payload.allWizardsValues,
          action.payload.fabricatorState,
        )
      }
      return Object.assign({}, state, {
        ...action.payload.cartState,
        checkoutPopupVisible: false,
        orderCompletePopupVisible: false,
      });
    }
    case types.CLEAR_CART:
      return {...state, cartItems: []};
    case types.SHOW_CHECKOUT_POPUP:
      return {...state, checkoutPopupVisible: action.payload.show};
    case types.SHOW_DEPOSIT:
      return {...state, depositVisible: action.payload.show, depositCallback: action.payload.callback};
    case types.SET_CART_ITEMS_ADDED_TO_PARENT_CART: {
      const cItemsAddedToParentCart = [...state.cItemsAddedToParentCart];
      action.payload.cartItems.forEach(cartItem => {
        const existing = cItemsAddedToParentCart.find(cItem => cItem.id === cartItem.id);
        if (existing) {
          existing.quantity += cartItem.quantity;
        } else {
          cItemsAddedToParentCart.push({
            id: cartItem.id,
            quantity: cartItem.quantity,
          })
        }
      });
      return {...state, cItemsAddedToParentCart};
    }
    case types.SET_LOOK_OPENED:
    case types.SET_LOOK_PAGE_OPENED:
      return state.cartItemEditFeature ? {...state, cartItemEditFeature: null} : state
    case types.SET_LOOK_ACTIVE_CATEGORY:
      return (state.cartItemEditFeature && !action.payload.lookActiveCategory && !action.payload.isMobile)
        ? {...state, cartItemEditFeature: null} : state
    case types.SET_LOOK_ACTIVE_FEATURE:
      return (state.cartItemEditFeature && !action.payload.lookActiveFeature) ? {...state, cartItemEditFeature: null} : state
    case types.TOGGLE_SELECTION_WINDOW:
      // exit editing on change route
      return (state.cartItemEditFeature && !action.payload.selectionOpened) ? {...state, cartItemEditFeature: null} : state
    case types.CHANGE_SELECTION_ACTIVE_PART_RELATED_INDEX:
      // exit editing on change step
      return state.cartItemEditFeature && !action.payload.isSameStep ? {...state, cartItemEditFeature: null} : state
    case types.SET_WIZARD_ACTIVE_STEP:
    case types.SET_WIZARD_SELECTED_VALUES: {
      let newState = state;
      if (state.displayLogicCartItems.length) {
        let newCartItems = state.cartItems;
        newCartItems = checkForAddToCartFeaturesWithDisplayLogic(
          newCartItems,
          state.displayLogicCartItems,
          action.payload.quoteIndex,
          action.payload.allWizardsValues,
          action.payload.fabricatorState,
        )
        newState = {...state, cartItems: newCartItems}
      }
      if ((action.type === types.SET_WIZARD_ACTIVE_STEP) && state.cartItemEditFeature) {
        newState = {...state, cartItemEditFeature: null}
      }
      return newState
    }
    case types.SET_MANUAL_TAX: {
      if (!state.manualTaxEntered) {
        state.manualTaxEntered = true;
      }
      state.sureTax = +action.payload.manualTax;
      return state
    }
    case types.TOGGLE_PRICE_WORKSHEET_OPENED: {
      if (state.hideTotalPricesBeforeWSOpened && action.payload.oldStateWorksheetOpened && !action.payload.priceWorksheetOpened) {
        state.hideTotalPricesBeforeWSOpened = false;
      }
      return state
    }
  }

  return state;
}
